import { useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import {
  ActivityLogContext,
  closeConfirmationModalAction,
  closeLogDrawerAction,
  markExerciseAsDoneAction,
  openConfirmationModalAction,
  openProgramDoneModalAction,
  saveDataAction,
  skipExerciseAction,
} from 'activity/ducks/index'
import { LayoutContext } from 'common/ducks/index'
import useApi from 'common/hooks/useApi'
import LRUCache from 'common/utils/LRUCache'
import DailyCache from 'common/utils/DailyCache'
import moment from 'moment'
import { UNITS } from 'constants/log.js'

const ENDPOINT = 'metrics/activities/'

export default function useLogForm() {
  const { state, dispatch } = useContext(ActivityLogContext)
  const { setAppLoading } = useContext(LayoutContext)
  const [conversionFactor, setConversionFactor] = useState(UNITS.m)
  const data = state.programDialog?.logDrawer?.data
  const [checked, setChecked] = useState(data?.kpi)
  const [formSubmtited, setFormSubmtited] = useState(false)
  const [addToLog, setAddToLog] = useState(false)

  const [{ loading: createLoading }, create] = useApi(
    {
      method: 'POST',
      url: ENDPOINT
    },
    { manual: true }
  )

  const [{ loading: updateLoading }] = useApi(
    {
      method: 'PUT'
    },
    { manual: true }
  )

  const cachedActivityNames = new LRUCache('activityNames', 50)
  const cachedSessionActivities = new DailyCache('sessionActivities')

  async function postForm(values) {
    const { day, distance, index, restTime, weight, workTime, tags, ...rest } =
      values

    const body = {
      ...rest,
      day: moment(day).toISOString(),
      distanceMeters: distance
        ? (distance * conversionFactor).toFixed(2)
        : null,
      restTimeSeconds: restTime,
      user: data?.user,
      weightLbs: weight,
      workTimeSeconds: workTime,
      kpi: checked,
      tags: tags || []
    }

    try {
      const response = await create({
        data: body
      })

      dispatch(
        saveDataAction({
          index,
          ...response.data
        })
      )

      if (values.name) {
        cachedSessionActivities.set(values.id, values)
      }

      setFormSubmtited(true)
      if (data?.id) {
        dispatch(markExerciseAsDoneAction(data.id))
      }
    } catch (e) {
      console.error(e)
    } finally {
      setAddToLog(true)
      setTimeout(() => {
        if (data?.isLastExercise && !state.programDialog.programDoneModal.open) {
          dispatch(openProgramDoneModalAction())
        }

        if (values.name) {
          cachedActivityNames.put(values.name)
        }
        if ( state.programDialog.startWorkoutDialog.open ) {
          dispatch(skipExerciseAction())
        }

        dispatch(closeLogDrawerAction())

      }, 1500);
    }
  }

  const {
    reps,
    setNumber = 0,
    weight
  } = cachedSessionActivities.get(data?.name) || {}

  const initialValues = {
    ...data,
    ...(data?.set ? { setNumber: data.set } : { setNumber: setNumber + 1 }),
    ...(data?.reps ? { reps: data.reps } : { reps }),
    ...(data?.weight ? { weight: data.weight } : { weight }),
    ...(data?.measuredWorkTime
      ? { workTime: data.measuredWorkTime }
      : { workTime: data.workTime }),
    setHidden: !data?.set,
    repsHidden: !data?.reps,
    weightHidden: !data?.weight,
    distanceHidden: (data?.distance <= 0),
    rirHidden: !data?.rir,
    tempoHidden: !data?.tempo,
    workTimeHidden: (data?.workTime <= 0),
    restTimeHidden: (data?.restTime <= 0),
    kpiHidden: !!data?.kpi,
    oneRepMaxHidden: (data?.oneRepMax <= 0) || data?.weight,
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: postForm
  })

  const defaultTextFieldOptions = {
    fullWidth: true,
    margin: 'normal',
    onChange: formik.handleChange,
    variant: 'outlined',
    InputLabelProps: {
      shrink: true
    },
    InputProps: {
      inputProps: {
        min: 0
      }
    },
    disabled: formSubmtited
  }

  const distanceFieldOptions = {
    fullWidth: false,
    margin: 'normal',
    onChange: formik.handleChange,
    variant: 'outlined',
    InputLabelProps: {
      shrink: true
    },
    InputProps: {
      inputProps: {
        min: 0
      }
    },
    disabled: formSubmtited
  }

  function updateConversionFactor(event) {
    setConversionFactor(event.target.value)
  }

  useEffect(() => {
    setAppLoading(createLoading || updateLoading)
  }, [createLoading, setAppLoading, updateLoading])

  const updateKpi = (event) => {
    setChecked(event.target.checked)
    formik.values.kpi = event.target.checked
  }

  const openConfirmationModal = (e) => {
    e.preventDefault()
    dispatch(openConfirmationModalAction('LOG_DRAWER'))
  }

  const closeConfirmationModal = () => {
    dispatch(closeConfirmationModalAction('LOG_DRAWER'))
  }

  const dataWithAdditionalFields = JSON.parse(JSON.stringify(data))
  if (dataWithAdditionalFields) {
    dataWithAdditionalFields.source = state.dialog?.data?.source
  }

  return {
    updateKpi,
    checked,
    formik,
    data: dataWithAdditionalFields,
    defaultTextFieldOptions,
    distanceFieldOptions,
    conversionFactor,
    updateConversionFactor,
    openConfirmationModal,
    closeConfirmationModal,
    state,
    postForm,
    formSubmtited,
    addToLog
  }
}
